import React, { useState, useEffect } from 'react';
import { Volume2, Settings, ChevronDown, ChevronUp, AlertTriangle, Mic, Zap, Crown, Sparkles, FolderOpen, History, Trash2, Play, Download, HardDrive, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import TTSButton from './TTSButton';

/**
 * Componente de control TTS con Edge-TTS, Higgs Audio y Qwen3-TTS
 * Edge-TTS: 79 voces en español, rápido, online
 * Higgs Audio: Voice cloning, emociones, lento (~15-20s)
 * Qwen3-TTS: #1 en calidad (WER 1.24), ultra-rápido (97ms), supera a ElevenLabs
 */
function TTSControls({ text, darkMode, questionId }) {
  const [showSettings, setShowSettings] = useState(false);

  // Motor TTS seleccionado
  const [ttsEngine, setTtsEngine] = useState(() => {
    return localStorage.getItem('ttsEngine') || 'edge';
  });

  const [ttsSpeed, setTtsSpeed] = useState(() => {
    const saved = localStorage.getItem('ttsSpeed');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [edgeTTSVoice, setEdgeTTSVoice] = useState(() => {
    return localStorage.getItem('edgeTTSVoice') || 'es-MX-DaliaNeural';
  });
  const [edgeTTSAvailable, setEdgeTTSAvailable] = useState(false);
  const [higgsAvailable, setHiggsAvailable] = useState(false);
  const [qwenAvailable, setQwenAvailable] = useState(false);
  const [availableVoices, setAvailableVoices] = useState(null);

  // Higgs voices
  const [higgsVoices, setHiggsVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState(() => {
    return localStorage.getItem('higgsVoiceId') || null;
  });
  const [temperature, setTemperature] = useState(() => {
    const saved = localStorage.getItem('higgsTemperatureBase');
    return saved ? parseFloat(saved) : 1.0;
  });

  // Higgs Audio params
  // Emotion presets optimizados para Higgs Audio v2
  const HIGGS_EMOTION_PRESETS = {
    neutral: { temperature: 0.30, topP: 0.90, label: 'Neutral', description: 'Profesional y calmado' },
    happy: { temperature: 0.45, topP: 0.95, label: 'Feliz', description: 'Alegre y enérgico' },
    sad: { temperature: 0.25, topP: 0.88, label: 'Triste', description: 'Melancólico y pausado' },
    angry: { temperature: 0.50, topP: 0.92, label: 'Enojado', description: 'Intenso y rápido' },
    surprised: { temperature: 0.55, topP: 0.95, label: 'Sorprendido', description: 'Expresivo y excitado' },
    questioning: { temperature: 0.35, topP: 0.92, label: 'Pregunta', description: 'Entonación interrogativa natural' },
  };

  const [higgsEmotion, setHiggsEmotion] = useState(() => {
    return localStorage.getItem('higgsEmotion') || 'neutral';
  });
  const [higgsTemperature, setHiggsTemperature] = useState(() => {
    const saved = localStorage.getItem('higgsTemperature');
    return saved ? parseFloat(saved) : HIGGS_EMOTION_PRESETS.neutral.temperature;
  });
  const [higgsTopP, setHiggsTopP] = useState(() => {
    const saved = localStorage.getItem('higgsTopP');
    return saved ? parseFloat(saved) : HIGGS_EMOTION_PRESETS.neutral.topP;
  });
  const [higgsSeed, setHiggsSeed] = useState(() => {
    const saved = localStorage.getItem('higgsSeed');
    return saved ? parseInt(saved) : null;
  });
  // Velocidad de reproducción (playbackRate) - NO afecta generación
  const [higgsPlaybackSpeed, setHiggsPlaybackSpeed] = useState(() => {
    const saved = localStorage.getItem('higgsPlaybackSpeed');
    return saved ? parseFloat(saved) : 1.0;
  });

  // Qwen3-TTS params
  const QWEN_SPEAKERS = {
    Ryan: { language: 'English', gender: 'male', description: 'Dinamica, fuerte ritmo (recomendada)' },
    Aiden: { language: 'English', gender: 'male', description: 'Americana, midrange claro' },
    Vivian: { language: 'Chinese', gender: 'female', description: 'Joven, brillante' },
    Serena: { language: 'Chinese', gender: 'female', description: 'Calida, gentil' },
    Uncle_Fu: { language: 'Chinese', gender: 'male', description: 'Experimentada, baja' },
    Dylan: { language: 'Chinese', gender: 'male', description: 'Beijing, clara, natural' },
    Eric: { language: 'Chinese', gender: 'male', description: 'Chengdu, vivaz' },
    Ono_Anna: { language: 'Japanese', gender: 'female', description: 'Japonesa, ludica' },
    Sohee: { language: 'Korean', gender: 'female', description: 'Coreana, calida' },
  };

  const QWEN_EMOTION_PRESETS = {
    neutral: { label: 'Neutral', instruct: '' },
    happy: { label: 'Feliz', instruct: 'Speak with a happy and cheerful tone.' },
    sad: { label: 'Triste', instruct: 'Speak with a sad and melancholic tone.' },
    angry: { label: 'Enojado', instruct: 'Speak with an angry and intense tone.' },
    surprised: { label: 'Sorprendido', instruct: 'Speak with a surprised and excited tone.' },
    questioning: { label: 'Pregunta', instruct: 'Speak with a curious, questioning intonation.' },
    professional: { label: 'Profesional', instruct: 'Speak in a professional and calm manner.' },
    enthusiastic: { label: 'Entusiasta', instruct: 'Speak with great enthusiasm and energy.' },
  };

  // Voice Design Presets
  const VOICE_DESIGN_PRESETS = {
    male_professional: { label: 'Profesional ♂', instruct: 'Deep male voice, professional and calm, like a news anchor with clear articulation.' },
    female_warm: { label: 'Calida ♀', instruct: 'Young female voice, warm and friendly, gentle tone with a slight smile.' },
    male_energetic: { label: 'Energetico ♂', instruct: 'Dynamic male voice, energetic and enthusiastic, like a sports commentator.' },
    female_authoritative: { label: 'Autoritaria ♀', instruct: 'Mature female voice, authoritative and confident, like a professor or expert.' },
    narrator_dramatic: { label: 'Narrador', instruct: 'Deep dramatic voice with theatrical flair, perfect for storytelling with varied pacing.' },
    child_curious: { label: 'Infantil', instruct: 'Young child voice, curious and playful, with wonder and excitement.' },
    elderly_wise: { label: 'Anciano', instruct: 'Elderly male voice, warm and wise, speaking slowly with gravitas and experience.' },
    custom: { label: 'Personalizado', instruct: '' },
  };

  const [qwenSpeaker, setQwenSpeaker] = useState(() => {
    return localStorage.getItem('qwenSpeaker') || 'Ryan';
  });
  const [qwenEmotion, setQwenEmotion] = useState(() => {
    return localStorage.getItem('qwenEmotion') || 'neutral';
  });
  const [qwenInstruct, setQwenInstruct] = useState(() => {
    return localStorage.getItem('qwenInstruct') || '';
  });
  const [qwenTemperature, setQwenTemperature] = useState(() => {
    const saved = localStorage.getItem('qwenTemperature');
    return saved ? parseFloat(saved) : 0.7;
  });
  const [qwenTopP, setQwenTopP] = useState(() => {
    const saved = localStorage.getItem('qwenTopP');
    return saved ? parseFloat(saved) : 0.9;
  });
  const [qwenSeed, setQwenSeed] = useState(() => {
    const saved = localStorage.getItem('qwenSeed');
    return saved ? parseInt(saved) : null;
  });
  const [qwenPlaybackSpeed, setQwenPlaybackSpeed] = useState(() => {
    const saved = localStorage.getItem('qwenPlaybackSpeed');
    return saved ? parseFloat(saved) : 1.0;
  });

  // Qwen Voice Cloning - Solo voice_clone soportado en servidor producción (8004)
  const [qwenMode, setQwenMode] = useState(() => {
    // Forzar voice_clone ya que el servidor 8004 solo soporta este modo
    return 'voice_clone';
  });
  const [qwenVoices, setQwenVoices] = useState([]);
  const [qwenVoiceId, setQwenVoiceId] = useState(() => {
    return localStorage.getItem('qwenVoiceId') || null;
  });

  // Qwen Voice Design
  const [qwenVoiceDesignPreset, setQwenVoiceDesignPreset] = useState(() => {
    return localStorage.getItem('qwenVoiceDesignPreset') || 'male_professional';
  });
  const [qwenVoiceDesignPrompt, setQwenVoiceDesignPrompt] = useState(() => {
    return localStorage.getItem('qwenVoiceDesignPrompt') || '';
  });

  // Qwen New (Flash) server availability
  const [qwenFlashAvailable, setQwenFlashAvailable] = useState(false);

  // ===== Qwen New (Flash) - Configuraciones separadas =====
  const [qwenNewTemperature, setQwenNewTemperature] = useState(() => {
    const saved = localStorage.getItem('qwenNewTemperature');
    return saved ? parseFloat(saved) : 0.7;
  });
  const [qwenNewTopP, setQwenNewTopP] = useState(() => {
    const saved = localStorage.getItem('qwenNewTopP');
    return saved ? parseFloat(saved) : 0.9;
  });
  const [qwenNewSeed, setQwenNewSeed] = useState(() => {
    const saved = localStorage.getItem('qwenNewSeed');
    return saved ? parseInt(saved) : null;
  });
  const [qwenNewPlaybackSpeed, setQwenNewPlaybackSpeed] = useState(() => {
    const saved = localStorage.getItem('qwenNewPlaybackSpeed');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [qwenNewVoiceId, setQwenNewVoiceId] = useState(() => {
    return localStorage.getItem('qwenNewVoiceId') || null;
  });
  const [qwenNewVoices, setQwenNewVoices] = useState([]);

  // Qwen Cache & History
  const [qwenCacheInfo, setQwenCacheInfo] = useState(null);
  const [qwenRecentAudios, setQwenRecentAudios] = useState([]);
  const [showQwenHistory, setShowQwenHistory] = useState(false);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [loadingCache, setLoadingCache] = useState(false);

  // Función para cambiar emoción Qwen y aplicar instruct
  const handleQwenEmotionChange = (emotion) => {
    setQwenEmotion(emotion);
    const preset = QWEN_EMOTION_PRESETS[emotion];
    if (preset) {
      setQwenInstruct(preset.instruct);
    }
  };

  // Función para cambiar emoción y aplicar preset
  const handleHiggsEmotionChange = (emotion) => {
    setHiggsEmotion(emotion);
    const preset = HIGGS_EMOTION_PRESETS[emotion];
    if (preset) {
      setHiggsTemperature(preset.temperature);
      setHiggsTopP(preset.topP);
    }
  };

  // Edge-TTS advanced params
  const [useNaturalSpeed, setUseNaturalSpeed] = useState(() => {
    const saved = localStorage.getItem('useNaturalSpeed');
    return saved === 'true';
  });
  const [ttsVolume, setTtsVolume] = useState(() => {
    const saved = localStorage.getItem('ttsVolume');
    return saved ? parseInt(saved) : 0;
  });
  const [ttsPitch, setTtsPitch] = useState(() => {
    const saved = localStorage.getItem('ttsPitch');
    return saved ? parseInt(saved) : 0;
  });

  // URLs de servidores TTS
  const EDGE_TTS_URL = process.env.REACT_APP_TTS_SERVER_URL || 'http://localhost:8001';
  const HIGGS_URL = process.env.REACT_APP_HIGGS_SERVER_URL || 'http://localhost:8003';
  const QWEN_URL_CMD = 'http://localhost:8004';
  const QWEN_URL_FLASH = 'http://localhost:8006';
  // URL del servidor Qwen activo segun motor seleccionado
  const currentQwenUrl = ttsEngine === 'qwen_new' ? QWEN_URL_FLASH : QWEN_URL_CMD;

  // Guardar preferencias
  useEffect(() => {
    localStorage.setItem('ttsEngine', ttsEngine);
  }, [ttsEngine]);

  useEffect(() => {
    localStorage.setItem('ttsSpeed', ttsSpeed.toString());
  }, [ttsSpeed]);

  useEffect(() => {
    localStorage.setItem('edgeTTSVoice', edgeTTSVoice);
  }, [edgeTTSVoice]);

  useEffect(() => {
    localStorage.setItem('useNaturalSpeed', useNaturalSpeed.toString());
  }, [useNaturalSpeed]);

  useEffect(() => {
    localStorage.setItem('ttsVolume', ttsVolume.toString());
  }, [ttsVolume]);

  useEffect(() => {
    localStorage.setItem('ttsPitch', ttsPitch.toString());
  }, [ttsPitch]);

  // Higgs voice preferences
  useEffect(() => {
    if (selectedVoiceId) localStorage.setItem('higgsVoiceId', selectedVoiceId);
  }, [selectedVoiceId]);

  // Higgs Audio preferences
  useEffect(() => {
    localStorage.setItem('higgsEmotion', higgsEmotion);
  }, [higgsEmotion]);

  useEffect(() => {
    localStorage.setItem('higgsTemperature', higgsTemperature.toString());
  }, [higgsTemperature]);

  useEffect(() => {
    localStorage.setItem('higgsTopP', higgsTopP.toString());
  }, [higgsTopP]);

  useEffect(() => {
    if (higgsSeed !== null) {
      localStorage.setItem('higgsSeed', higgsSeed.toString());
    } else {
      localStorage.removeItem('higgsSeed');
    }
  }, [higgsSeed]);

  useEffect(() => {
    localStorage.setItem('higgsPlaybackSpeed', higgsPlaybackSpeed.toString());
  }, [higgsPlaybackSpeed]);

  // Qwen3-TTS preferences
  useEffect(() => {
    localStorage.setItem('qwenSpeaker', qwenSpeaker);
  }, [qwenSpeaker]);

  useEffect(() => {
    localStorage.setItem('qwenEmotion', qwenEmotion);
  }, [qwenEmotion]);

  useEffect(() => {
    localStorage.setItem('qwenInstruct', qwenInstruct);
  }, [qwenInstruct]);

  useEffect(() => {
    localStorage.setItem('qwenTemperature', qwenTemperature.toString());
  }, [qwenTemperature]);

  useEffect(() => {
    localStorage.setItem('qwenTopP', qwenTopP.toString());
  }, [qwenTopP]);

  useEffect(() => {
    if (qwenSeed !== null) {
      localStorage.setItem('qwenSeed', qwenSeed.toString());
    } else {
      localStorage.removeItem('qwenSeed');
    }
  }, [qwenSeed]);

  useEffect(() => {
    localStorage.setItem('qwenPlaybackSpeed', qwenPlaybackSpeed.toString());
  }, [qwenPlaybackSpeed]);

  useEffect(() => {
    localStorage.setItem('qwenMode', qwenMode);
  }, [qwenMode]);

  useEffect(() => {
    if (qwenVoiceId) {
      localStorage.setItem('qwenVoiceId', qwenVoiceId);
    } else {
      localStorage.removeItem('qwenVoiceId');
    }
  }, [qwenVoiceId]);

  // Qwen Voice Design preferences
  useEffect(() => {
    localStorage.setItem('qwenVoiceDesignPreset', qwenVoiceDesignPreset);
  }, [qwenVoiceDesignPreset]);

  useEffect(() => {
    localStorage.setItem('qwenVoiceDesignPrompt', qwenVoiceDesignPrompt);
  }, [qwenVoiceDesignPrompt]);

  // ===== Qwen New preferences =====
  useEffect(() => {
    localStorage.setItem('qwenNewTemperature', qwenNewTemperature.toString());
  }, [qwenNewTemperature]);

  useEffect(() => {
    localStorage.setItem('qwenNewTopP', qwenNewTopP.toString());
  }, [qwenNewTopP]);

  useEffect(() => {
    if (qwenNewSeed !== null) {
      localStorage.setItem('qwenNewSeed', qwenNewSeed.toString());
    } else {
      localStorage.removeItem('qwenNewSeed');
    }
  }, [qwenNewSeed]);

  useEffect(() => {
    localStorage.setItem('qwenNewPlaybackSpeed', qwenNewPlaybackSpeed.toString());
  }, [qwenNewPlaybackSpeed]);

  useEffect(() => {
    if (qwenNewVoiceId) {
      localStorage.setItem('qwenNewVoiceId', qwenNewVoiceId);
    } else {
      localStorage.removeItem('qwenNewVoiceId');
    }
  }, [qwenNewVoiceId]);

  // Verificar disponibilidad de todos los servidores
  useEffect(() => {
    checkEdgeTTSAvailability();
    checkHiggsAvailability();
    checkQwenAvailability();
    loadAvailableVoices();
  }, []);

  const checkEdgeTTSAvailability = async () => {
    try {
      const response = await fetch(`${EDGE_TTS_URL}/api/tts/status`);
      if (response.ok) {
        setEdgeTTSAvailable(true);
      }
    } catch (error) {
      setEdgeTTSAvailable(false);
    }
  };

  const checkHiggsAvailability = async () => {
    try {
      const response = await fetch(`${HIGGS_URL}/api/tts/status`);
      if (response.ok) {
        setHiggsAvailable(true);
        loadHiggsVoices();
      }
    } catch (error) {
      setHiggsAvailable(false);
    }
  };

  const loadHiggsVoices = async () => {
    try {
      const response = await fetch(`${HIGGS_URL}/api/voices`);
      if (response.ok) {
        const data = await response.json();
        setHiggsVoices(data.voices || []);
      }
    } catch (error) {
      console.error('Error cargando voces Higgs:', error);
    }
  };

  const checkQwenAvailability = async () => {
    // Check CMD server (8004)
    try {
      const response = await fetch(`${QWEN_URL_CMD}/api/tts/status`);
      if (response.ok) {
        setQwenAvailable(true);
        loadQwenVoices();
      }
    } catch (error) {
      setQwenAvailable(false);
    }
    // Check Flash server (8006)
    try {
      const response = await fetch(`${QWEN_URL_FLASH}/api/tts/status`);
      if (response.ok) {
        setQwenFlashAvailable(true);
        loadQwenNewVoices();
      }
    } catch (error) {
      setQwenFlashAvailable(false);
    }
  };

  const loadQwenVoices = async () => {
    // Cargar voces del servidor CMD
    try {
      const response = await fetch(`${QWEN_URL_CMD}/api/voices`);
      if (response.ok) {
        const data = await response.json();
        setQwenVoices(data.voices || []);
      }
    } catch (error) {
      console.error('Error cargando voces Qwen:', error);
    }
  };

  const loadQwenNewVoices = async () => {
    // Cargar voces del servidor Flash (8006)
    try {
      const response = await fetch(`${QWEN_URL_FLASH}/api/voices`);
      if (response.ok) {
        const data = await response.json();
        setQwenNewVoices(data.voices || []);
      }
    } catch (error) {
      console.error('Error cargando voces Qwen New:', error);
    }
  };

  const loadAvailableVoices = async () => {
    try {
      const response = await fetch(`${EDGE_TTS_URL}/api/tts/voices`);
      if (response.ok) {
        const data = await response.json();
        setAvailableVoices(data);
      }
    } catch (error) {
      console.error('Error cargando voces:', error);
    }
  };

  // ===== Qwen Cache Functions =====
  const loadQwenCacheInfo = async () => {
    try {
      const response = await fetch(`${currentQwenUrl}/api/tts/cache/info`);
      if (response.ok) {
        const data = await response.json();
        setQwenCacheInfo(data);
      }
    } catch (error) {
      console.error('Error cargando info cache Qwen:', error);
    }
  };

  const loadQwenRecentAudios = async () => {
    setLoadingCache(true);
    try {
      const response = await fetch(`${currentQwenUrl}/api/tts/cache/recent?limit=10`);
      if (response.ok) {
        const data = await response.json();
        setQwenRecentAudios(data.audios || []);
      }
    } catch (error) {
      console.error('Error cargando audios recientes:', error);
    } finally {
      setLoadingCache(false);
    }
  };

  const openQwenCacheFolder = async () => {
    try {
      await fetch(`${currentQwenUrl}/api/tts/cache/open`);
    } catch (error) {
      console.error('Error abriendo carpeta:', error);
    }
  };

  const clearQwenCache = async () => {
    if (!window.confirm('¿Eliminar todos los audios en cache?')) return;
    try {
      const response = await fetch(`${currentQwenUrl}/api/tts/cache`, { method: 'DELETE' });
      if (response.ok) {
        loadQwenCacheInfo();
        loadQwenRecentAudios();
      }
    } catch (error) {
      console.error('Error limpiando cache:', error);
    }
  };

  const playRecentAudio = (audioUrl) => {
    const fullUrl = `${currentQwenUrl}${audioUrl}`;
    const audio = new Audio(fullUrl);
    setPlayingAudioId(audioUrl);
    audio.onended = () => setPlayingAudioId(null);
    audio.onerror = () => setPlayingAudioId(null);
    audio.play();
  };

  const playLastAudio = async () => {
    // Si ya tenemos audios cargados, reproducir el primero (más reciente)
    if (qwenRecentAudios.length > 0) {
      playRecentAudio(qwenRecentAudios[0].audio_url);
      return;
    }
    // Si no hay audios cargados, cargarlos primero
    try {
      const response = await fetch(`${currentQwenUrl}/api/tts/cache/recent?limit=1`);
      if (response.ok) {
        const data = await response.json();
        if (data.audios && data.audios.length > 0) {
          playRecentAudio(data.audios[0].audio_url);
        }
      }
    } catch (error) {
      console.error('Error cargando último audio:', error);
    }
  };

  const downloadAudio = (audioUrl, filename) => {
    const fullUrl = `${currentQwenUrl}${audioUrl}`;
    const a = document.createElement('a');
    a.href = fullUrl;
    a.download = filename || 'qwen-audio.wav';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Cargar audios recientes cuando Qwen está disponible (para botón "reproducir último")
  useEffect(() => {
    if (qwenAvailable) {
      loadQwenCacheInfo();
      loadQwenRecentAudios();
    }
  }, [qwenAvailable]);

  // Refrescar cuando se abre el historial
  useEffect(() => {
    if (showQwenHistory && qwenAvailable) {
      loadQwenCacheInfo();
      loadQwenRecentAudios();
    }
  }, [showQwenHistory]);

  const quickSpeeds = [0.75, 1.0, 1.25, 1.5, 1.75, 2.0];
  const speedRange = {
    min: 0.5,
    max: 2.0,
    step: 0.05,
  };

  // 🆕 FUNCIONES DE CONVERSIÓN
  const speedToRate = (speed) => {
    // Convierte speed (0.5-2.0) a rate ("+0%", "-50%", "+100%")
    const percentage = Math.round((speed - 1.0) * 100);
    return percentage >= 0 ? `+${percentage}%` : `${percentage}%`;
  };

  const volumeToEdge = (vol) => {
    // vol: -50 a +50 → Edge: "-50%" a "+50%"
    return vol >= 0 ? `+${vol}%` : `${vol}%`;
  };

  const pitchToEdge = (hz) => {
    // hz: -100 a +100 → Edge: "-100Hz" a "+100Hz"
    return hz >= 0 ? `+${hz}Hz` : `${hz}Hz`;
  };

  // Determinar si el motor actual está disponible
  const isCurrentEngineAvailable =
    ttsEngine === 'edge' ? edgeTTSAvailable :
    ttsEngine === 'higgs' ? higgsAvailable :
    ttsEngine === 'qwen_new' ? qwenFlashAvailable :
    qwenAvailable;

  return (
    <div className="flex flex-col gap-2">
      {/* Botón principal con selector de motor */}
      <div className="flex items-center gap-2">
        {/* Selector de motor TTS */}
        <div className="flex rounded-lg overflow-hidden border border-gray-600/30">
          <button
            onClick={() => setTtsEngine('edge')}
            className={`
              px-2 py-1.5 text-xs font-medium flex items-center gap-1 transition-all
              ${ttsEngine === 'edge'
                ? darkMode
                  ? 'bg-cyan-600 text-white'
                  : 'bg-blue-500 text-white'
                : darkMode
                  ? 'bg-slate-700/50 text-gray-400 hover:text-gray-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
              ${!edgeTTSAvailable ? 'opacity-50' : ''}
            `}
            title={edgeTTSAvailable ? 'Edge-TTS: Rápido, 79 voces' : 'Edge-TTS no disponible'}
          >
            <Zap className="w-3 h-3" />
            Edge
            {edgeTTSAvailable && <span className="w-1.5 h-1.5 bg-green-400 rounded-full ml-1" />}
          </button>
          <button
            onClick={() => setTtsEngine('higgs')}
            className={`
              px-2 py-1.5 text-xs font-medium flex items-center gap-1 transition-all
              ${ttsEngine === 'higgs'
                ? darkMode
                  ? 'bg-amber-600 text-white'
                  : 'bg-amber-500 text-white'
                : darkMode
                  ? 'bg-slate-700/50 text-gray-400 hover:text-gray-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
              ${!higgsAvailable ? 'opacity-50' : ''}
            `}
            title={higgsAvailable ? 'Higgs: Voice cloning, emociones (~15-20s)' : 'Higgs Audio no disponible'}
          >
            <Crown className="w-3 h-3" />
            Higgs
            {higgsAvailable && <span className="w-1.5 h-1.5 bg-green-400 rounded-full ml-1" />}
          </button>
          <button
            onClick={() => setTtsEngine('qwen')}
            className={`
              px-2 py-1.5 text-xs font-medium flex items-center gap-1 transition-all
              ${ttsEngine === 'qwen'
                ? darkMode
                  ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-lg shadow-purple-500/30'
                  : 'bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg shadow-purple-400/30'
                : darkMode
                  ? 'bg-slate-700/50 text-gray-400 hover:text-gray-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
              ${!qwenAvailable ? 'opacity-50' : ''}
            `}
            title={qwenAvailable ? 'Qwen: Original CMD (8004)' : 'Qwen no disponible'}
          >
            <Sparkles className="w-3 h-3" />
            Qwen
            {qwenAvailable && <span className="w-1.5 h-1.5 bg-purple-400 rounded-full ml-1 animate-pulse" />}
          </button>
          <button
            onClick={() => setTtsEngine('qwen_new')}
            className={`
              px-2 py-1.5 text-xs font-medium flex items-center gap-1 transition-all
              ${ttsEngine === 'qwen_new'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/30'
                : darkMode
                  ? 'bg-slate-700/50 text-gray-400 hover:text-gray-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
              ${!qwenFlashAvailable ? 'opacity-50' : ''}
            `}
            title={qwenFlashAvailable ? 'Qwen New: WSL + Flash Attention (8006)' : 'Qwen New no disponible'}
          >
            <Zap className="w-3 h-3" />
            Qwen New
            {qwenFlashAvailable && <span className="w-1.5 h-1.5 bg-amber-400 rounded-full ml-1 animate-pulse" />}
          </button>
        </div>

        {/* Botón TTS */}
        <div className="relative group">
          <div className={!isCurrentEngineAvailable ? 'opacity-50 cursor-not-allowed' : ''}>
            <TTSButton
              text={text}
              darkMode={darkMode}
              questionId={`${questionId}_${ttsEngine}`}
              engine={ttsEngine === 'qwen_new' ? 'qwen' : ttsEngine}
              voice={edgeTTSVoice}
              language="es"
              speed={ttsSpeed}
              rate={useNaturalSpeed ? speedToRate(ttsSpeed) : "+0%"}
              volume={volumeToEdge(ttsVolume)}
              pitch={pitchToEdge(ttsPitch)}
              useNaturalSpeed={useNaturalSpeed}
              temperature={ttsEngine === 'higgs' ? higgsTemperature : temperature}
              seed={higgsSeed}
              voiceId={selectedVoiceId}
              higgsEmotion={higgsEmotion}
              higgsTopP={higgsTopP}
              higgsPlaybackSpeed={higgsPlaybackSpeed}
              qwenSpeaker={qwenSpeaker}
              qwenInstruct={qwenInstruct}
              qwenEmotion={qwenEmotion}
              qwenTemperature={ttsEngine === 'qwen_new' ? qwenNewTemperature : qwenTemperature}
              qwenTopP={ttsEngine === 'qwen_new' ? qwenNewTopP : qwenTopP}
              qwenSeed={ttsEngine === 'qwen_new' ? qwenNewSeed : qwenSeed}
              qwenPlaybackSpeed={ttsEngine === 'qwen_new' ? qwenNewPlaybackSpeed : qwenPlaybackSpeed}
              qwenMode={qwenMode}
              qwenVoiceId={ttsEngine === 'qwen_new' ? qwenNewVoiceId : qwenVoiceId}
              qwenVoiceDesignPreset={qwenVoiceDesignPreset}
              qwenVoiceDesignPrompt={qwenVoiceDesignPrompt}
              qwenServerUrl={currentQwenUrl}
              size="normal"
              showLabel={false}
              className={`transition-all ${!isCurrentEngineAvailable ? 'pointer-events-none' : ''}`}
            />
          </div>
          <div className={`
            absolute -bottom-6 left-1/2 -translate-x-1/2
            px-2 py-0.5 rounded text-xs whitespace-nowrap z-10
            pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity
            ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-700 text-white'}
          `}>
            {ttsEngine === 'edge' ? (
              <><Zap className="w-3 h-3 inline mr-1" />Edge-TTS</>
            ) : ttsEngine === 'higgs' ? (
              <><Crown className="w-3 h-3 inline mr-1" />Higgs (~15-20s)</>
            ) : ttsEngine === 'qwen_new' ? (
              <><Zap className="w-3 h-3 inline mr-1 text-amber-400" />Qwen New (Flash)</>
            ) : (
              <><Sparkles className="w-3 h-3 inline mr-1 text-purple-400" />Qwen (CMD)</>
            )}
          </div>
        </div>

        {/* Botón de configuración */}
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`
            p-2 rounded-lg transition-all
            ${darkMode
              ? 'bg-slate-700/50 hover:bg-slate-600/60 text-gray-300 hover:text-gray-100'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800'}
            ${showSettings ? 'ring-2 ring-blue-500/50' : ''}
          `}
          title="Configuración TTS"
        >
          <Settings className={`w-4 h-4 ${showSettings ? 'rotate-90' : ''} transition-transform`} />
        </button>
      </div>

      {/* Panel de configuración expandible */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={`
              p-3 rounded-lg border mt-2
              ${darkMode
                ? 'bg-slate-800/50 border-slate-700/50'
                : 'bg-gray-50 border-gray-200'}
            `}>
              {/* ========== CONTROLES EDGE-TTS ========== */}
              {ttsEngine === 'edge' && (
                <>
                  {/* Control de velocidad */}
                  <div className="mb-4">
                    <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1 block`}>
                      Velocidad de reproducción ({ttsSpeed.toFixed(2)}x)
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={speedRange.min}
                        max={speedRange.max}
                        step={speedRange.step}
                        value={ttsSpeed}
                        onChange={(e) => setTtsSpeed(parseFloat(e.target.value))}
                        className="flex-1 accent-cyan-500"
                      />
                      <span className={`text-sm font-semibold ${darkMode ? 'text-cyan-300' : 'text-sky-600'}`}>
                        {ttsSpeed.toFixed(2)}x
                      </span>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {quickSpeeds.map((value) => (
                        <button
                          key={value}
                          onClick={() => setTtsSpeed(value)}
                          className={`
                            px-2 py-1 text-xs rounded transition-all
                            ${ttsSpeed === value
                              ? darkMode
                                ? 'bg-cyan-600 text-white'
                                : 'bg-blue-500 text-white'
                              : darkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }
                          `}
                        >
                          {value.toFixed(2)}x
                        </button>
                      ))}
                    </div>

                    {/* Toggle de Velocidad Natural */}
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        id="useNaturalSpeed"
                        checked={useNaturalSpeed}
                        onChange={(e) => setUseNaturalSpeed(e.target.checked)}
                        className="w-4 h-4 accent-cyan-500 cursor-pointer"
                      />
                      <label
                        htmlFor="useNaturalSpeed"
                        className={`text-xs cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        Velocidad Natural (IA genera a esta velocidad)
                      </label>
                    </div>
                    {useNaturalSpeed && (
                      <div className={`mt-1 text-xs ${darkMode ? 'text-cyan-400' : 'text-blue-600'}`}>
                        Rate: {speedToRate(ttsSpeed)} - Audio generado naturalmente por IA
                      </div>
                    )}
                  </div>

                  {/* Control de Volumen */}
                  <div className="mb-4">
                    <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1 block`}>
                      Volumen ({volumeToEdge(ttsVolume)})
                    </label>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-50%</span>
                      <input
                        type="range"
                        min="-50"
                        max="50"
                        step="5"
                        value={ttsVolume}
                        onChange={(e) => setTtsVolume(parseInt(e.target.value))}
                        className="flex-1 accent-cyan-500"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>+50%</span>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-cyan-300' : 'text-sky-600'} min-w-[50px] text-right`}>
                        {volumeToEdge(ttsVolume)}
                      </span>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {[-20, -10, 0, 10, 20].map((value) => (
                        <button
                          key={value}
                          onClick={() => setTtsVolume(value)}
                          className={`
                            px-2 py-1 text-xs rounded transition-all
                            ${ttsVolume === value
                              ? darkMode
                                ? 'bg-cyan-600 text-white'
                                : 'bg-blue-500 text-white'
                              : darkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }
                          `}
                        >
                          {value >= 0 ? `+${value}%` : `${value}%`}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Control de Tono */}
                  <div className="mb-4">
                    <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1 block`}>
                      Tono ({pitchToEdge(ttsPitch)})
                    </label>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Grave</span>
                      <input
                        type="range"
                        min="-100"
                        max="100"
                        step="1"
                        value={ttsPitch}
                        onChange={(e) => setTtsPitch(parseInt(e.target.value))}
                        className="flex-1 accent-cyan-500"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Agudo</span>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-cyan-300' : 'text-sky-600'} min-w-[60px] text-right`}>
                        {pitchToEdge(ttsPitch)}
                      </span>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {[-50, -25, 0, 25, 50].map((value) => (
                        <button
                          key={value}
                          onClick={() => setTtsPitch(value)}
                          className={`
                            px-2 py-1 text-xs rounded transition-all
                            ${ttsPitch === value
                              ? darkMode
                                ? 'bg-cyan-600 text-white'
                                : 'bg-blue-500 text-white'
                              : darkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }
                          `}
                        >
                          {value >= 0 ? `+${value}Hz` : `${value}Hz`}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Selector de voz Edge-TTS */}
                  <div className="mb-3">
                    <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1 block`}>
                      Voz Edge-TTS (Español)
                    </label>
                    <select
                      value={edgeTTSVoice}
                      onChange={(e) => setEdgeTTSVoice(e.target.value)}
                      className={`
                        w-full px-2 py-1 text-sm rounded border
                        ${darkMode
                          ? 'bg-gray-700 border-gray-600 text-gray-200'
                          : 'bg-white border-gray-300 text-gray-700'}
                      `}
                    >
                      <optgroup label="Recomendadas">
                        <option value="es-MX-DaliaNeural">México - Dalia (Femenina)</option>
                        <option value="es-ES-ElviraNeural">España - Elvira (Femenina)</option>
                        <option value="es-AR-ElenaNeural">Argentina - Elena (Femenina)</option>
                        <option value="es-CO-SalomeNeural">Colombia - Salomé (Femenina)</option>
                      </optgroup>
                      <optgroup label="Masculinas">
                        <option value="es-MX-JorgeNeural">México - Jorge</option>
                        <option value="es-ES-AlvaroNeural">España - Álvaro</option>
                        <option value="es-AR-TomasNeural">Argentina - Tomás</option>
                        <option value="es-CO-GonzaloNeural">Colombia - Gonzalo</option>
                      </optgroup>
                      <optgroup label="Otras regiones">
                        <option value="es-US-PalomaNeural">US Latino - Paloma (F)</option>
                        <option value="es-PE-CamilaNeural">Perú - Camila (F)</option>
                        <option value="es-CL-CatalinaNeural">Chile - Catalina (F)</option>
                        <option value="es-VE-PaolaNeural">Venezuela - Paola (F)</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Estado de Edge-TTS */}
                  <div className={`
                    p-2 rounded-lg text-xs
                    ${darkMode ? 'bg-gray-900/50' : 'bg-gray-100'}
                  `}>
                    <div className="flex items-center gap-2">
                      {edgeTTSAvailable ? (
                        <>
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          <span className={darkMode ? 'text-green-400' : 'text-green-600'}>
                            Edge-TTS conectado
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 bg-red-500 rounded-full" />
                          <span className={darkMode ? 'text-red-400' : 'text-red-600'}>
                            Edge-TTS no disponible
                          </span>
                        </>
                      )}
                    </div>
                    {!edgeTTSAvailable && (
                      <div className={`mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                        <p>Ejecuta: <code className="bg-black/20 px-1 rounded">start-edge-tts.bat</code></p>
                      </div>
                    )}
                  </div>

                  {/* Info Edge-TTS */}
                  <div className={`
                    mt-3 p-2 rounded-lg text-xs flex items-start gap-2
                    ${darkMode ? 'bg-cyan-900/20 text-cyan-400' : 'bg-blue-50 text-blue-700'}
                  `}>
                    <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Edge-TTS (Microsoft)</p>
                      <ul className="mt-1 space-y-0.5">
                        <li>• 79 voces en español</li>
                        <li>• Rápido (~0.5s)</li>
                        <li>• Gratuito, requiere internet</li>
                      </ul>
                    </div>
                  </div>
                </>
              )}

              {/* ========== CONTROLES HIGGS AUDIO ========== */}
              {ttsEngine === 'higgs' && (
                <>
                  {/* Advertencia de tiempo */}
                  <div className={`
                    mb-4 p-2 rounded-lg text-xs flex items-start gap-2
                    ${darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-700'}
                  `}>
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Higgs Audio tarda ~15-20 segundos por audio</p>
                      <p className="mt-1 opacity-80">Buena calidad, pero no es tiempo real. Usa Qwen para mejor calidad Y velocidad.</p>
                    </div>
                  </div>

                  {/* Selector de Emocion con Presets */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Emoción
                      </label>
                      <button
                        onClick={() => handleHiggsEmotionChange(higgsEmotion)}
                        className={`text-xs ${darkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-500'}`}
                        title="Restaurar valores del preset"
                      >
                        Restaurar preset
                      </button>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {Object.entries(HIGGS_EMOTION_PRESETS).map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => handleHiggsEmotionChange(key)}
                          className={`
                            px-3 py-1.5 text-xs rounded transition-all
                            ${higgsEmotion === key
                              ? darkMode
                                ? 'bg-amber-600 text-white'
                                : 'bg-amber-500 text-white'
                              : darkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }
                          `}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    {/* Descripción del preset actual */}
                    <p className={`mt-1.5 text-xs ${darkMode ? 'text-amber-400/80' : 'text-amber-600/80'}`}>
                      {HIGGS_EMOTION_PRESETS[higgsEmotion]?.description}
                    </p>
                  </div>

                  {/* Control Temperature */}
                  <div className="mb-4">
                    <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1 block`}>
                      Aleatoriedad ({higgsTemperature.toFixed(2)})
                    </label>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Predecible</span>
                      <input
                        type="range"
                        min="0.1"
                        max="1.5"
                        step="0.05"
                        value={higgsTemperature}
                        onChange={(e) => setHiggsTemperature(parseFloat(e.target.value))}
                        className="flex-1 accent-amber-500"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Variado</span>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-600'} min-w-[40px] text-right`}>
                        {higgsTemperature.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {[0.3, 0.5, 0.7, 0.9, 1.2].map((value) => (
                        <button
                          key={value}
                          onClick={() => setHiggsTemperature(value)}
                          className={`
                            px-2 py-1 text-xs rounded transition-all
                            ${higgsTemperature === value
                              ? darkMode
                                ? 'bg-amber-600 text-white'
                                : 'bg-amber-500 text-white'
                              : darkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }
                          `}
                        >
                          {value.toFixed(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Control Top-P */}
                  <div className="mb-4">
                    <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1 block`}>
                      Top-P ({higgsTopP.toFixed(2)})
                    </label>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Enfocado</span>
                      <input
                        type="range"
                        min="0.5"
                        max="1.0"
                        step="0.05"
                        value={higgsTopP}
                        onChange={(e) => setHiggsTopP(parseFloat(e.target.value))}
                        className="flex-1 accent-amber-500"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Diverso</span>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-600'} min-w-[40px] text-right`}>
                        {higgsTopP.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Control Velocidad de Reproducción (playbackRate) */}
                  <div className="mb-4">
                    <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1 block`}>
                      Velocidad de reproducción ({higgsPlaybackSpeed.toFixed(2)}x)
                    </label>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>0.5x</span>
                      <input
                        type="range"
                        min="0.5"
                        max="2.0"
                        step="0.05"
                        value={higgsPlaybackSpeed}
                        onChange={(e) => setHiggsPlaybackSpeed(parseFloat(e.target.value))}
                        className="flex-1 accent-amber-500"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>2.0x</span>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-600'} min-w-[50px] text-right`}>
                        {higgsPlaybackSpeed.toFixed(2)}x
                      </span>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {[0.75, 1.0, 1.25, 1.5, 1.75, 2.0].map((value) => (
                        <button
                          key={value}
                          onClick={() => setHiggsPlaybackSpeed(value)}
                          className={`
                            px-2 py-1 text-xs rounded transition-all
                            ${higgsPlaybackSpeed === value
                              ? darkMode
                                ? 'bg-amber-600 text-white'
                                : 'bg-amber-500 text-white'
                              : darkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }
                          `}
                        >
                          {value.toFixed(2)}x
                        </button>
                      ))}
                    </div>
                    <p className={`mt-1.5 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Cambia velocidad sin regenerar el audio
                    </p>
                  </div>

                  {/* Control Seed */}
                  <div className="mb-4">
                    <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1 block`}>
                      Seed (reproducibilidad)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="999999"
                        placeholder="Aleatorio"
                        value={higgsSeed ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setHiggsSeed(val === '' ? null : parseInt(val));
                        }}
                        className={`
                          flex-1 px-2 py-1 text-sm rounded border
                          ${darkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'}
                        `}
                      />
                      <button
                        onClick={() => setHiggsSeed(Math.floor(Math.random() * 999999))}
                        className={`
                          px-2 py-1 text-xs rounded transition-all
                          ${darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}
                        `}
                        title="Generar seed aleatorio"
                      >
                        Aleatorio
                      </button>
                      <button
                        onClick={() => setHiggsSeed(null)}
                        className={`
                          px-2 py-1 text-xs rounded transition-all
                          ${darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}
                        `}
                        title="Limpiar seed"
                      >
                        Limpiar
                      </button>
                    </div>
                  </div>

                  {/* Selector de voz clonada */}
                  <div className="mb-3">
                    <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1 block`}>
                      Voz clonada {higgsVoices.length > 0 ? `(${higgsVoices.length} disponibles)` : ''}
                    </label>
                    <select
                      value={selectedVoiceId || ''}
                      onChange={(e) => setSelectedVoiceId(e.target.value || null)}
                      className={`
                        w-full px-2 py-1 text-sm rounded border
                        ${darkMode
                          ? 'bg-gray-700 border-gray-600 text-gray-200'
                          : 'bg-white border-gray-300 text-gray-700'}
                      `}
                    >
                      <option value="">Voz por defecto</option>
                      {higgsVoices.map((voice) => (
                        <option key={voice.id} value={voice.id}>
                          {voice.name} {voice.description ? `- ${voice.description}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Estado de Higgs */}
                  <div className={`
                    p-2 rounded-lg text-xs
                    ${darkMode ? 'bg-gray-900/50' : 'bg-gray-100'}
                  `}>
                    <div className="flex items-center gap-2">
                      {higgsAvailable ? (
                        <>
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          <span className={darkMode ? 'text-green-400' : 'text-green-600'}>
                            Higgs Audio conectado (GPU)
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 bg-red-500 rounded-full" />
                          <span className={darkMode ? 'text-red-400' : 'text-red-600'}>
                            Higgs Audio no disponible
                          </span>
                        </>
                      )}
                    </div>
                    {!higgsAvailable && (
                      <div className={`mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                        <p>Ejecuta: <code className="bg-black/20 px-1 rounded">start-higgs.bat</code></p>
                      </div>
                    )}
                  </div>

                  {/* Info Higgs */}
                  <div className={`
                    mt-3 p-2 rounded-lg text-xs flex items-start gap-2
                    ${darkMode ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-700'}
                  `}>
                    <Crown className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Higgs Audio v2 (Boson AI)</p>
                      <ul className="mt-1 space-y-0.5">
                        <li>• Buena calidad emocional</li>
                        <li>• 10M+ horas de entrenamiento</li>
                        <li>• 50+ idiomas soportados</li>
                        <li>• Lento (~15-20s), requiere ~18GB VRAM</li>
                      </ul>
                    </div>
                  </div>
                </>
              )}

              {/* ========== CONTROLES QWEN3-TTS PREMIUM ========== */}
              {(ttsEngine === 'qwen' || ttsEngine === 'qwen_new') && (
                <div className="space-y-4">
                  {/* Header Premium con gradiente */}
                  <div className={`
                    relative overflow-hidden rounded-xl p-4
                    ${ttsEngine === 'qwen_new'
                      ? darkMode
                        ? 'bg-gradient-to-br from-amber-900/50 via-orange-900/40 to-yellow-900/30 border border-amber-500/30'
                        : 'bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border border-amber-200'
                      : darkMode
                        ? 'bg-gradient-to-br from-purple-900/50 via-violet-900/40 to-fuchsia-900/30 border border-purple-500/30'
                        : 'bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50 border border-purple-200'}
                  `}>
                    <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl ${ttsEngine === 'qwen_new' ? 'from-amber-500/20' : 'from-purple-500/20'} to-transparent rounded-full blur-2xl`} />
                    <div className={`absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr ${ttsEngine === 'qwen_new' ? 'from-orange-500/10' : 'from-violet-500/10'} to-transparent rounded-full blur-xl`} />
                    <div className="relative flex items-center gap-3">
                      <div className={`
                        w-12 h-12 rounded-xl flex items-center justify-center
                        ${ttsEngine === 'qwen_new'
                          ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/40'
                          : darkMode
                            ? 'bg-gradient-to-br from-purple-500 to-violet-600 shadow-lg shadow-purple-500/40'
                            : 'bg-gradient-to-br from-purple-400 to-violet-500 shadow-lg shadow-purple-400/40'}
                      `}>
                        {ttsEngine === 'qwen_new' ? <Zap className="w-6 h-6 text-white" /> : <Sparkles className="w-6 h-6 text-white" />}
                      </div>
                      <div>
                        <h3 className={`font-bold text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {ttsEngine === 'qwen_new' ? 'Qwen New (Flash)' : 'Qwen3-TTS Premium'}
                        </h3>
                        <p className={`text-xs ${ttsEngine === 'qwen_new' ? (darkMode ? 'text-amber-300' : 'text-amber-600') : (darkMode ? 'text-purple-300' : 'text-purple-600')}`}>
                          {ttsEngine === 'qwen_new' ? 'WSL + Flash Attention • Puerto 8006' : 'CMD Original • Puerto 8004'}
                        </p>
                      </div>
                      {/* Status indicator */}
                      <div className={`
                        ml-auto flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium
                        ${(ttsEngine === 'qwen_new' ? qwenFlashAvailable : qwenAvailable)
                          ? ttsEngine === 'qwen_new'
                            ? darkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'
                            : darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'
                          : darkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}
                      `}>
                        <span className={`w-2 h-2 rounded-full ${(ttsEngine === 'qwen_new' ? qwenFlashAvailable : qwenAvailable) ? (ttsEngine === 'qwen_new' ? 'bg-amber-400' : 'bg-purple-400') + ' animate-pulse' : 'bg-red-500'}`} />
                        {(ttsEngine === 'qwen_new' ? qwenFlashAvailable : qwenAvailable) ? 'Online' : 'Offline'}
                      </div>
                    </div>
                  </div>

                  {/* Voice Clone Panel - Único modo soportado en servidor producción */}
                  <div className={`
                    p-3 rounded-xl border
                    ${ttsEngine === 'qwen_new'
                      ? darkMode ? 'bg-slate-800/30 border-amber-500/20' : 'bg-amber-50/50 border-amber-200'
                      : darkMode ? 'bg-slate-800/30 border-purple-500/20' : 'bg-purple-50/50 border-purple-200'}
                  `}>
                    <label className={`text-xs font-semibold ${ttsEngine === 'qwen_new' ? (darkMode ? 'text-amber-300' : 'text-amber-700') : (darkMode ? 'text-purple-300' : 'text-purple-700')} mb-2 flex items-center gap-1`}>
                      <Mic className="w-3 h-3" />
                      Voces Clonadas
                      <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                        {ttsEngine === 'qwen_new' ? 'Flash 8006' : 'CMD 8004'}
                      </span>
                    </label>
                    <p className={`text-[10px] mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {ttsEngine === 'qwen_new' ? 'Voces del servidor Flash (WSL)' : 'Para Custom Voice y Voice Design, usa Qwen TTS Studio (puerto 8005)'}
                    </p>
                    {(ttsEngine === 'qwen_new' ? qwenNewVoices : qwenVoices).length > 0 ? (
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {(ttsEngine === 'qwen_new' ? qwenNewVoices : qwenVoices).map((voice) => (
                          <button
                            key={voice.id}
                            onClick={() => ttsEngine === 'qwen_new' ? setQwenNewVoiceId(voice.id) : setQwenVoiceId(voice.id)}
                            className={`
                              w-full p-2 rounded-lg flex items-center gap-2 transition-all
                              ${(ttsEngine === 'qwen_new' ? qwenNewVoiceId : qwenVoiceId) === voice.id
                                ? darkMode
                                  ? ttsEngine === 'qwen_new' ? 'bg-amber-600/30 border border-amber-500/50' : 'bg-purple-600/30 border border-purple-500/50'
                                  : ttsEngine === 'qwen_new' ? 'bg-amber-100 border border-amber-300' : 'bg-purple-100 border border-purple-300'
                                : darkMode
                                  ? 'bg-slate-700/50 border border-transparent hover:border-slate-600'
                                  : 'bg-white border border-gray-200 hover:border-gray-300'}
                            `}
                          >
                            <div className={`
                              w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                              ${(ttsEngine === 'qwen_new' ? qwenNewVoiceId : qwenVoiceId) === voice.id
                                ? ttsEngine === 'qwen_new'
                                  ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white'
                                  : 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
                                : darkMode ? 'bg-slate-600 text-gray-300' : 'bg-gray-200 text-gray-600'}
                            `}>
                              {voice.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 text-left">
                              <p className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                {voice.name}
                              </p>
                              <p className={`text-[10px] ${voice.has_transcript
                                ? darkMode ? 'text-emerald-400' : 'text-emerald-600'
                                : darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                {voice.has_transcript ? '✓ Con transcript' : '⚠ Sin transcript'}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className={`text-center py-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <Mic className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-xs">Sin voces clonadas</p>
                        <p className="text-[10px] mt-1">{ttsEngine === 'qwen_new' ? 'Sin voces en servidor Flash' : 'Sube audios en Qwen TTS Studio (puerto 8005)'}</p>
                      </div>
                    )}
                  </div>

                  {/* Advanced Settings Compact */}
                  <details className={`
                    rounded-xl border overflow-hidden
                    ${darkMode ? 'bg-slate-800/30 border-slate-700/50' : 'bg-gray-50 border-gray-200'}
                  `}>
                    <summary className={`
                      px-3 py-2 cursor-pointer flex items-center gap-2 text-xs font-semibold
                      ${darkMode ? 'text-gray-300 hover:bg-slate-700/30' : 'text-gray-600 hover:bg-gray-100'}
                    `}>
                      <Settings className="w-3 h-3" />
                      Configuracion Avanzada
                      <ChevronDown className="w-3 h-3 ml-auto" />
                    </summary>
                    <div className="p-3 space-y-4 border-t border-inherit">
                      {/* Temperature Slider */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Temperature</span>
                          <span className={`text-xs font-mono font-bold ${ttsEngine === 'qwen_new' ? (darkMode ? 'text-amber-400' : 'text-amber-600') : (darkMode ? 'text-purple-400' : 'text-purple-600')}`}>
                            {(ttsEngine === 'qwen_new' ? qwenNewTemperature : qwenTemperature).toFixed(2)}
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0.1"
                          max="1.5"
                          step="0.05"
                          value={ttsEngine === 'qwen_new' ? qwenNewTemperature : qwenTemperature}
                          onChange={(e) => ttsEngine === 'qwen_new' ? setQwenNewTemperature(parseFloat(e.target.value)) : setQwenTemperature(parseFloat(e.target.value))}
                          className={`w-full h-2 rounded-full appearance-none cursor-pointer ${ttsEngine === 'qwen_new' ? 'accent-amber-500' : 'accent-purple-500'}`}
                          style={{
                            background: darkMode
                              ? `linear-gradient(to right, ${ttsEngine === 'qwen_new' ? '#f59e0b' : '#a855f7'} 0%, ${ttsEngine === 'qwen_new' ? '#f59e0b' : '#a855f7'} ${(((ttsEngine === 'qwen_new' ? qwenNewTemperature : qwenTemperature) - 0.1) / 1.4) * 100}%, #1e293b ${(((ttsEngine === 'qwen_new' ? qwenNewTemperature : qwenTemperature) - 0.1) / 1.4) * 100}%, #1e293b 100%)`
                              : `linear-gradient(to right, ${ttsEngine === 'qwen_new' ? '#f59e0b' : '#a855f7'} 0%, ${ttsEngine === 'qwen_new' ? '#f59e0b' : '#a855f7'} ${(((ttsEngine === 'qwen_new' ? qwenNewTemperature : qwenTemperature) - 0.1) / 1.4) * 100}%, #e5e7eb ${(((ttsEngine === 'qwen_new' ? qwenNewTemperature : qwenTemperature) - 0.1) / 1.4) * 100}%, #e5e7eb 100%)`
                          }}
                        />
                        <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                          <span>Consistente</span>
                          <span>Variado</span>
                        </div>
                      </div>

                      {/* Top-P Slider */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Top-P</span>
                          <span className={`text-xs font-mono font-bold ${ttsEngine === 'qwen_new' ? (darkMode ? 'text-orange-400' : 'text-orange-600') : (darkMode ? 'text-violet-400' : 'text-violet-600')}`}>
                            {(ttsEngine === 'qwen_new' ? qwenNewTopP : qwenTopP).toFixed(2)}
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0.5"
                          max="1.0"
                          step="0.05"
                          value={ttsEngine === 'qwen_new' ? qwenNewTopP : qwenTopP}
                          onChange={(e) => ttsEngine === 'qwen_new' ? setQwenNewTopP(parseFloat(e.target.value)) : setQwenTopP(parseFloat(e.target.value))}
                          className={`w-full h-2 rounded-full appearance-none cursor-pointer ${ttsEngine === 'qwen_new' ? 'accent-orange-500' : 'accent-violet-500'}`}
                          style={{
                            background: darkMode
                              ? `linear-gradient(to right, ${ttsEngine === 'qwen_new' ? '#f97316' : '#8b5cf6'} 0%, ${ttsEngine === 'qwen_new' ? '#f97316' : '#8b5cf6'} ${(((ttsEngine === 'qwen_new' ? qwenNewTopP : qwenTopP) - 0.5) / 0.5) * 100}%, #1e293b ${(((ttsEngine === 'qwen_new' ? qwenNewTopP : qwenTopP) - 0.5) / 0.5) * 100}%, #1e293b 100%)`
                              : `linear-gradient(to right, ${ttsEngine === 'qwen_new' ? '#f97316' : '#8b5cf6'} 0%, ${ttsEngine === 'qwen_new' ? '#f97316' : '#8b5cf6'} ${(((ttsEngine === 'qwen_new' ? qwenNewTopP : qwenTopP) - 0.5) / 0.5) * 100}%, #e5e7eb ${(((ttsEngine === 'qwen_new' ? qwenNewTopP : qwenTopP) - 0.5) / 0.5) * 100}%, #e5e7eb 100%)`
                          }}
                        />
                      </div>

                      {/* Speed Pills */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Velocidad</span>
                          <span className={`text-xs font-mono font-bold ${darkMode ? 'text-fuchsia-400' : 'text-fuchsia-600'}`}>
                            {(ttsEngine === 'qwen_new' ? qwenNewPlaybackSpeed : qwenPlaybackSpeed).toFixed(2)}x
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {[0.75, 1.0, 1.25, 1.5, 2.0].map((value) => (
                            <button
                              key={value}
                              onClick={() => ttsEngine === 'qwen_new' ? setQwenNewPlaybackSpeed(value) : setQwenPlaybackSpeed(value)}
                              className={`
                                flex-1 py-1 text-[10px] font-medium rounded transition-all
                                ${(ttsEngine === 'qwen_new' ? qwenNewPlaybackSpeed : qwenPlaybackSpeed) === value
                                  ? darkMode
                                    ? 'bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white'
                                    : 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white'
                                  : darkMode
                                    ? 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}
                              `}
                            >
                              {value}x
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Seed */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Seed</span>
                        </div>
                        <div className="flex gap-1">
                          <input
                            type="number"
                            min="0"
                            max="999999"
                            placeholder="Aleatorio"
                            value={(ttsEngine === 'qwen_new' ? qwenNewSeed : qwenSeed) ?? ''}
                            onChange={(e) => {
                              const val = e.target.value === '' ? null : parseInt(e.target.value);
                              ttsEngine === 'qwen_new' ? setQwenNewSeed(val) : setQwenSeed(val);
                            }}
                            className={`
                              flex-1 px-2 py-1 text-xs rounded-lg border
                              ${darkMode
                                ? 'bg-slate-900/50 border-slate-600 text-gray-200 placeholder-gray-500'
                                : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'}
                            `}
                          />
                          <button
                            onClick={() => {
                              const randomSeed = Math.floor(Math.random() * 999999);
                              ttsEngine === 'qwen_new' ? setQwenNewSeed(randomSeed) : setQwenSeed(randomSeed);
                            }}
                            className={`px-2 py-1 text-[10px] rounded-lg ${darkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}
                          >
                            Random
                          </button>
                        </div>
                      </div>

                    </div>
                  </details>

                  {/* ===== Cache & History Panel ===== */}
                  <div className={`
                    rounded-xl border overflow-hidden
                    ${darkMode ? 'bg-slate-800/30 border-purple-500/20' : 'bg-purple-50/30 border-purple-200'}
                  `}>
                    {/* Header con botones de acción */}
                    <div className={`
                      px-3 py-2 flex items-center justify-between border-b
                      ${darkMode ? 'border-purple-500/20' : 'border-purple-200'}
                    `}>
                      <button
                        onClick={() => setShowQwenHistory(!showQwenHistory)}
                        className={`flex items-center gap-2 text-xs font-semibold ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}
                      >
                        <History className="w-3.5 h-3.5" />
                        Historial de Audios
                        <ChevronDown className={`w-3 h-3 transition-transform ${showQwenHistory ? 'rotate-180' : ''}`} />
                      </button>
                      <div className="flex items-center gap-1">
                        {/* Botón reproducir último */}
                        <button
                          onClick={playLastAudio}
                          disabled={!qwenAvailable || qwenRecentAudios.length === 0}
                          className={`
                            p-1.5 rounded-lg transition-all
                            ${playingAudioId
                              ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white animate-pulse'
                              : darkMode
                                ? 'hover:bg-purple-500/20 text-purple-400 hover:text-purple-300'
                                : 'hover:bg-purple-100 text-purple-600 hover:text-purple-700'}
                            ${!qwenAvailable || qwenRecentAudios.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                          `}
                          title="Reproducir último audio"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        {/* Botón abrir carpeta */}
                        <button
                          onClick={openQwenCacheFolder}
                          disabled={!qwenAvailable}
                          className={`
                            p-1.5 rounded-lg transition-all
                            ${darkMode
                              ? 'hover:bg-purple-500/20 text-purple-400 hover:text-purple-300'
                              : 'hover:bg-purple-100 text-purple-600 hover:text-purple-700'}
                            ${!qwenAvailable ? 'opacity-50 cursor-not-allowed' : ''}
                          `}
                          title="Abrir carpeta de audios"
                        >
                          <FolderOpen className="w-4 h-4" />
                        </button>
                        {/* Botón refrescar */}
                        <button
                          onClick={() => { loadQwenCacheInfo(); loadQwenRecentAudios(); }}
                          disabled={!qwenAvailable || loadingCache}
                          className={`
                            p-1.5 rounded-lg transition-all
                            ${darkMode
                              ? 'hover:bg-purple-500/20 text-purple-400 hover:text-purple-300'
                              : 'hover:bg-purple-100 text-purple-600 hover:text-purple-700'}
                            ${!qwenAvailable || loadingCache ? 'opacity-50' : ''}
                          `}
                          title="Refrescar"
                        >
                          <RefreshCw className={`w-4 h-4 ${loadingCache ? 'animate-spin' : ''}`} />
                        </button>
                        {/* Botón limpiar cache */}
                        <button
                          onClick={clearQwenCache}
                          disabled={!qwenAvailable}
                          className={`
                            p-1.5 rounded-lg transition-all
                            ${darkMode
                              ? 'hover:bg-red-500/20 text-red-400 hover:text-red-300'
                              : 'hover:bg-red-100 text-red-500 hover:text-red-600'}
                            ${!qwenAvailable ? 'opacity-50 cursor-not-allowed' : ''}
                          `}
                          title="Limpiar cache"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Stats del cache */}
                    {qwenCacheInfo && (
                      <div className={`
                        px-3 py-2 flex items-center gap-4 text-[10px] border-b
                        ${darkMode ? 'bg-slate-900/30 border-purple-500/10 text-gray-400' : 'bg-white/50 border-purple-100 text-gray-500'}
                      `}>
                        <div className="flex items-center gap-1">
                          <HardDrive className="w-3 h-3" />
                          <span>{qwenCacheInfo.files_count} archivos</span>
                        </div>
                        <div>{qwenCacheInfo.total_size_mb} MB</div>
                        {qwenCacheInfo.newest_file && (
                          <div className="ml-auto">
                            Último: {new Date(qwenCacheInfo.newest_file).toLocaleString('es', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Lista de audios recientes */}
                    <AnimatePresence>
                      {showQwenHistory && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          exit={{ height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="p-2 space-y-1 max-h-48 overflow-y-auto">
                            {loadingCache ? (
                              <div className={`text-center py-4 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
                                Cargando...
                              </div>
                            ) : qwenRecentAudios.length > 0 ? (
                              qwenRecentAudios.map((audio) => (
                                <div
                                  key={audio.id}
                                  className={`
                                    p-2 rounded-lg flex items-center gap-2 transition-all
                                    ${darkMode
                                      ? 'bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50'
                                      : 'bg-white hover:bg-gray-50 border border-gray-200'}
                                  `}
                                >
                                  {/* Play button */}
                                  <button
                                    onClick={() => playRecentAudio(audio.audio_url)}
                                    className={`
                                      w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all
                                      ${playingAudioId === audio.audio_url
                                        ? 'bg-gradient-to-br from-purple-500 to-violet-500 text-white animate-pulse'
                                        : darkMode
                                          ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                                          : 'bg-purple-100 text-purple-600 hover:bg-purple-200'}
                                    `}
                                  >
                                    <Play className="w-3.5 h-3.5 ml-0.5" />
                                  </button>
                                  {/* Info */}
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-[10px] font-mono truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      {audio.id.slice(0, 16)}...
                                    </p>
                                    <div className={`flex items-center gap-2 text-[9px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      {audio.duration && <span>{audio.duration}s</span>}
                                      <span>{audio.size_kb} KB</span>
                                      <span>{new Date(audio.created_at).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                  </div>
                                  {/* Download */}
                                  <button
                                    onClick={() => downloadAudio(audio.audio_url, audio.filename)}
                                    className={`
                                      p-1.5 rounded-lg transition-all flex-shrink-0
                                      ${darkMode
                                        ? 'hover:bg-slate-600 text-gray-400 hover:text-gray-200'
                                        : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'}
                                    `}
                                    title="Descargar"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))
                            ) : (
                              <div className={`text-center py-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-xs">Sin audios generados</p>
                                <p className="text-[10px] mt-1">Los audios aparecerán aquí</p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Server status footer */}
                  {!qwenAvailable && (
                    <div className={`
                      p-3 rounded-xl text-xs text-center
                      ${darkMode ? 'bg-red-900/20 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}
                    `}>
                      <p className="font-medium">Servidor no disponible</p>
                      <code className={`text-[10px] ${darkMode ? 'bg-black/30' : 'bg-white'} px-2 py-0.5 rounded mt-1 inline-block`}>
                        .\venv_qwen\Scripts\python.exe qwen_server.py
                      </code>
                    </div>
                  )}
                </div>
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TTSControls;
